import { Table as ChakraTable, Box, Grid, Spinner, TableRowProps } from '@chakra-ui/react';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { WorksheetData } from 'pages/in-app/worksheet/WorksheetDashboard';
import { DefaultCell } from './Cells/DefaultCell';
import {
  UserData,
  Column,
  VoucherData,
  ClassData,
  actionsColumnIdentifier,
  ParticipantData,
  ClassWorksheetsData,
  WorksheetStudentData,
  WorksheetTemplateData,
} from './types/Column';

type TableDataType =
  | UserData
  | VoucherData
  | WorksheetData
  | ClassData
  | ParticipantData
  | ClassWorksheetsData
  | WorksheetStudentData
  | WorksheetTemplateData;

type Props<T> = {
  columns: Column<T>[];
  data: T[];
  isDataLoading: boolean;
  onSort?: (column: keyof T, direction: 'asc' | 'desc') => void;
  sortBy?: string;
  sortDirection?: string;
  onRowClick?: (id: string, extra?: Record<string, unknown>) => void; // always object
  tableProps?: ChakraTable.RootProps;
  tableRowProps?: TableRowProps;
  tableHeadProps?: ChakraTable.HeaderProps;
  scrollable?: boolean;
};

const TableHeader = <T extends TableDataType>({
  columns,
  sortColumn,
  sortOrder,
  onSort,

  tableHeadProps,
}: {
  columns: Column<T>[];
  sortColumn?: keyof T;
  sortOrder?: string;
  onSort?: (column: keyof T, direction: 'asc' | 'desc') => void;
  tableHeadProps?: ChakraTable.HeaderProps;
}): ReactElement => (
  <ChakraTable.Header {...tableHeadProps}>
    <ChakraTable.Row>
      {columns.map((column) => {
        const isSorted = sortColumn === column.accessor;
        // eslint-disable-next-line no-nested-ternary
        const direction = isSorted ? (sortOrder === 'asc' ? '↑' : '↓') : '';
        return (
          <ChakraTable.ColumnHeader
            padding="0 24px 20px 0"
            key={`${column.header}-${column.accessor.toString()}`}
            textAlign="left"
            textTransform="none"
            textStyle="body.xs.medium"
            color="text.tertiary"
            onClick={() =>
              !column.sortDisabled &&
              onSort &&
              onSort(column.accessor, sortOrder === 'asc' ? 'asc' : 'desc')
            }
            cursor={!column.sortDisabled ? 'pointer' : 'default'}
            aria-disabled
            userSelect="none"
          >
            {column.header} {direction}
          </ChakraTable.ColumnHeader>
        );
      })}
    </ChakraTable.Row>
  </ChakraTable.Header>
);
const TableRow = <T extends TableDataType>({
  row,
  columns,
  rowIndex,
  onClick,
  tableRowProps,
}: {
  row: T;
  columns: Column<T>[];
  rowIndex: number;
  onClick?: (id: string) => void;
  tableRowProps?: TableRowProps;
}): React.ReactElement => {
  const renderCell = (column: Column<T>): React.ReactElement => {
    const isAction = column.accessor.toString() === actionsColumnIdentifier;
    const cellValue = row[column.accessor];

    if (isAction) {
      return cellValue as React.ReactElement;
    }

    // Check if the value is already a React element
    if (React.isValidElement(cellValue)) {
      return cellValue;
    }

    if (!column.element) {
      return <DefaultCell label={String(cellValue)} />;
    }

    const Element = column.element;
    return <Element {...(column.getCellProps?.(row) || {})} label={String(cellValue)} />;
  };

  const { expandedRow } = row as { expandedRow?: React.ReactElement };

  return (
    <>
      <ChakraTable.Row
        key={`row-${row.id}` || `row-${rowIndex}`}
        _hover={{ bg: 'background.canvas' }}
        _active={{ bg: 'background.canvas' }}
        onClick={onClick ? () => onClick(row.id) : undefined}
        {...tableRowProps}
      >
        {columns.map((column) => (
          <ChakraTable.Cell key={column.accessor.toString()} height="60px" padding="0 10px 0 0">
            {renderCell(column)}
          </ChakraTable.Cell>
        ))}
      </ChakraTable.Row>
      {expandedRow}
    </>
  );
};

export const Table = <T extends TableDataType>({
  columns,
  data,
  isDataLoading,
  onSort,
  sortBy,
  sortDirection,
  onRowClick,
  tableProps,
  tableRowProps,
  tableHeadProps,
  scrollable = false,
}: Props<T>): ReactElement => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<string>('asc');

  useEffect(() => {
    setSortColumn(sortBy ?? null);
    setSortOrder(sortDirection ?? 'asc');
  }, [sortBy, sortDirection]);
  const handleSort = (column: keyof T): void => {
    const isSameColumn = sortColumn === column;
    const newDirection = isSameColumn && sortOrder === 'asc' ? 'desc' : 'asc';

    setSortColumn(column as string);
    setSortOrder(newDirection);
    onSort?.(column, newDirection);
  };

  const loadingSpinner = useMemo(
    () => (
      <ChakraTable.Row>
        <ChakraTable.Cell colSpan={columns.length}>
          <Grid placeItems="center" width="100%">
            <Spinner
              m="10%"
              borderWidth="7px"
              animationDuration="1s"
              color="text.accent"
              size="xl"
            />
          </Grid>
        </ChakraTable.Cell>
      </ChakraTable.Row>
    ),
    [columns.length]
  );

  const tableContent = (
    <ChakraTable.Root columnGap="200px" {...tableProps}>
      <TableHeader
        columns={columns}
        sortColumn={sortColumn as keyof T | undefined}
        sortOrder={sortOrder}
        onSort={handleSort}
        tableHeadProps={tableHeadProps}
      />
      <ChakraTable.Body width="100%">
        {isDataLoading
          ? loadingSpinner
          : data?.map((row, rowIndex) => (
              <TableRow
                key={row.id || rowIndex}
                row={row}
                columns={columns}
                rowIndex={rowIndex}
                onClick={onRowClick ? () => onRowClick(row.id) : undefined}
                tableRowProps={tableRowProps}
              />
            ))}
      </ChakraTable.Body>
    </ChakraTable.Root>
  );

  return (
    <Box width="100%">
      {scrollable ? (
        <ChakraTable.ScrollArea width="100%">{tableContent}</ChakraTable.ScrollArea>
      ) : (
        tableContent
      )}
    </Box>
  );
};
